/**
 * How close a CRM plan's end date is — shared by the site-wide header banner
 * (shared/plan-expiry-banner) and the Settings → Plan tab, so the two cannot
 * disagree about when a plan counts as "expiring soon".
 */

import type { SubscriptionState } from '../admin/plans.types';

/** Highlight the plan's expiry once this many days (or fewer) remain. */
export const PLAN_EXPIRY_WARNING_DAYS = 3;

export interface PlanExpiry {
  /** Negative once the end date has passed. 0 means the plan ends today. */
  readonly daysLeft: number;
  readonly isExpired: boolean;
  readonly isExpiringSoon: boolean;
}

/**
 * Days until `crm.clinic_subscriptions.end_date`, date-only with no time-of-day.
 * Null when the plan has no end date (e.g. a trial with no fixed term) — the
 * caller renders nothing rather than guessing.
 *
 * `end_date` is a plain SQL `date` carrying no timezone, so it is parsed as
 * LOCAL midnight and compared against a local midnight today. Do not "fix" this
 * to UTC: the clinic's own calendar day is what the date means, and shifting it
 * would flip the banner a day early or late for anyone east or west of UTC.
 *
 * `today` exists for tests; callers leave it out.
 */
export function planExpiryFromEndDate(
  endDate: string | null | undefined,
  today: Date = new Date(),
): PlanExpiry | null {
  if (!endDate) {
    return null;
  }

  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(end.getTime())) {
    return null;
  }

  const midnight = new Date(today);
  midnight.setHours(0, 0, 0, 0);

  const daysLeft = Math.round((end.getTime() - midnight.getTime()) / 86_400_000);

  return {
    daysLeft,
    isExpired: daysLeft < 0,
    isExpiringSoon: daysLeft >= 0 && daysLeft <= PLAN_EXPIRY_WARNING_DAYS,
  };
}

/** What the CRM knows about the clinic's plan when deciding whether to warn (PlanAccessService). */
export interface PlanNoticeInput {
  readonly state: SubscriptionState | null;
  /** crm.clinic_subscriptions.end_date, date-only. */
  readonly endDate: string | null | undefined;
  /** The last day of grace access (get_my_plan().grace_until), date-only. */
  readonly graceUntil: string | null | undefined;
  /** The clinic owner (users_tb.user_type_id 2) — the one person who can renew. */
  readonly isOwner: boolean;
  /** Whether CRM enforcement is on (Super admin › Plans), i.e. grace really does end in a lockout. */
  readonly enforced: boolean;
}

/**
 * One line for the site-wide banner. `key` is a planBanner.* translation that
 * takes `{{count}}` (= `days`) and, for the grace messages, `{{date}}` (= `date`,
 * date-only — the caller formats it).
 */
export interface PlanNotice {
  readonly kind: 'ending' | 'grace' | 'expired';
  readonly key: string;
  /** Whole days left (of the plan, or of grace access); 0 means today is the last day. */
  readonly days: number;
  readonly date?: string;
  /** Red rather than amber. */
  readonly severe: boolean;
}

/**
 * Who is told what, and when — the same rules as the clinic app.
 *
 * Everyone is told when the subscription is about to end: its last few days
 * (amber), so nobody is surprised.
 *
 * Renewing is the OWNER's job, so the owner also hears about the whole of grace
 * (red for its last few days) and an expired plan that is still open only
 * because enforcement is off. Staff are told about grace only in its last few
 * days, and only when enforcement will actually lock them out, that access is
 * ending and the owner is the person to talk to.
 *
 * none and cancelled return null: both are blocked, and the /plans page says
 * what happened. A grace without a grace_until also returns null rather than
 * inventing a deadline (the server always sends one).
 */
export function planNotice(input: PlanNoticeInput, today: Date = new Date()): PlanNotice | null {
  const { state, endDate, graceUntil, isOwner, enforced } = input;

  switch (state) {
    case 'trial':
    case 'active': {
      const expiry = planExpiryFromEndDate(endDate, today);
      if (!expiry?.isExpiringSoon) return null;
      const days = expiry.daysLeft;
      return {
        kind: 'ending',
        key: days >= 1 ? 'planBanner.endsIn' : 'planBanner.endsToday',
        days,
        date: endDate ?? undefined,
        severe: false,
      };
    }

    case 'grace': {
      const grace = planExpiryFromEndDate(graceUntil, today);
      if (!grace) return null;
      // A grace_until already behind us is the server and this clock disagreeing
      // by a day around midnight; "ends today" is the honest reading of that.
      const days = Math.max(0, grace.daysLeft);
      const lastDays = days <= PLAN_EXPIRY_WARNING_DAYS;
      if (isOwner) {
        return {
          kind: 'grace',
          key: days >= 1 ? 'planBanner.graceOwner' : 'planBanner.graceOwnerToday',
          days,
          date: endDate ?? undefined,
          severe: lastDays,
        };
      }
      if (!enforced || !lastDays) return null;
      return {
        kind: 'grace',
        key: days >= 1 ? 'planBanner.graceStaff' : 'planBanner.graceStaffToday',
        days,
        severe: true,
      };
    }

    case 'expired':
      // Only reachable inside the app while enforcement is off; once it is on,
      // an expired plan is blocked and the user is on /plans instead.
      if (!isOwner) return null;
      return { kind: 'expired', key: 'planBanner.expiredOwner', days: 0, date: endDate ?? undefined, severe: true };

    default:
      return null;
  }
}
